import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProductStockInput } from "@/lib/validations/stock";
import type { Database } from "@/integrations/supabase/types";

type ProductStockSoftDeleteFields = {
  deleted_at?: string | null;
  deleted_by?: string | null;
};

type ProductStockInsert = Database["public"]["Tables"]["product_stock"]["Insert"] &
  ProductStockSoftDeleteFields;

type ProductStockUpdate = Database["public"]["Tables"]["product_stock"]["Update"] &
  ProductStockSoftDeleteFields;

export interface ProductStock {
  id: string;
  item_id: string;
  quantity: number;
  min_expiry_date: string | null;
  has_expiring_items: boolean;
  clinic_id: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
  items?: {
    id: string;
    name: string;
    unit: string;
    track_by_batch: boolean;
    track_by_expiry: boolean;
    product_subtype_id: string | null;
    product_subtype?: { name: string } | null;
  };
}

export interface ProductStockFilters {
  search?: string;
  hasExpiringItems?: boolean;
  productSubtypeIds?: string[];
}

/**
 * Get current clinic_id from user's profile
 */
async function getCurrentClinicId(): Promise<string> {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;

  const userId = authData?.user?.id;
  if (!userId) throw new Error("Usuário não autenticado");

  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("current_clinic_id")
    .eq("id", userId)
    .single();

  if (profileError) throw profileError;
  if (!profile?.current_clinic_id) throw new Error("Nenhuma clínica selecionada");

  return profile.current_clinic_id;
}

async function relabelLatestStockMovementAsAdjustment({
  clinicId,
  itemId,
  previousQuantity,
  nextQuantity,
  reason,
}: {
  clinicId: string;
  itemId: string;
  previousQuantity: number;
  nextQuantity: number;
  reason?: string | null;
}) {
  if (!reason) return;
  if (previousQuantity === nextQuantity) return;

  const { data: movement } = await supabase
    .from("product_stock_movements")
    .select("id")
    .eq("clinic_id", clinicId)
    .eq("item_id", itemId)
    .eq("quantity_before", previousQuantity)
    .eq("quantity_after", nextQuantity)
    .is("reason", null)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!movement?.id) return;

  const delta = nextQuantity - previousQuantity;
  const { error } = await supabase
    .from("product_stock_movements")
    .update({
      type: "AJUSTE",
      quantity: delta,
      reason,
    })
    .eq("id", movement.id);

  if (error) throw error;
}

/**
 * Hook to fetch all product stock
 */
export function useProductStock(filters?: ProductStockFilters) {
  return useQuery<ProductStock[]>({
    queryKey: ["product-stock", filters],
    queryFn: async () => {
      const clinicId = await getCurrentClinicId();

      let query = supabase
        .from("product_stock")
        .select(`
          *,
          items:item_id (
            id,
            name,
            unit,
            track_by_batch,
            track_by_expiry,
            product_subtype_id,
            product_subtype:product_subtypes(name)
          )
        `)
        .eq("clinic_id", clinicId)
        .order("updated_at", { ascending: false });

      if (filters?.hasExpiringItems) {
        query = query.eq("has_expiring_items", true);
      }

      if (filters?.search) {
        // Filter by item name (requires a join or subquery)
        // We'll filter client-side for now since Supabase doesn't support filtering on joined tables easily
      }

      const { data, error } = await query;
      if (error) throw error;

      let results = (data || []) as unknown as ProductStock[];

      // Hide soft-deleted records (schema has deleted_at/deleted_by, but generated TS types may lag behind).
      results = results.filter((stock) => stock.deleted_at == null);

      // Client-side search filter
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        results = results.filter((stock) =>
          stock.items?.name.toLowerCase().includes(searchLower) ||
          stock.items?.id.toLowerCase().includes(searchLower)
        );
      }

      // Client-side subtype filter (multiple selection)
      if (filters?.productSubtypeIds?.length) {
        const ids = new Set(filters.productSubtypeIds);
        results = results.filter(
          (stock) =>
            stock.items?.product_subtype_id &&
            ids.has(stock.items.product_subtype_id)
        );
      }

      return results;
    },
  });
}

/**
 * Hook to fetch a single product stock by item_id
 */
export function useProductStockByItem(itemId: string) {
  return useQuery<ProductStock | null>({
    queryKey: ["product-stock", itemId],
    queryFn: async () => {
      const clinicId = await getCurrentClinicId();

      const { data, error } = await supabase
        .from("product_stock")
        .select(`
          *,
          items:item_id (
            id,
            name,
            unit,
            track_by_batch,
            track_by_expiry,
            product_subtype_id,
            product_subtype:product_subtypes(name)
          )
        `)
        .eq("item_id", itemId)
        .eq("clinic_id", clinicId)
        .maybeSingle();

      if (error) throw error;
      const row = (data as unknown as ProductStock) || null;
      if (row?.deleted_at != null) return null;
      return row;
    },
    enabled: !!itemId,
  });
}

/**
 * Hook to add or update product stock
 */
export function useAddOrUpdateProductStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ProductStockInput & { expiry_date?: string | null; adjustment_reason?: string | null }) => {
      const clinicId = await getCurrentClinicId();

      // Check if stock already exists for this item
      const { data: existing } = await supabase
        .from("product_stock")
        .select("id, quantity, deleted_at")
        .eq("item_id", input.item_id)
        .eq("clinic_id", clinicId)
        .maybeSingle();

      // Get item info to check if it tracks expiry
      const { data: item } = await supabase
        .from("items")
        .select("track_by_expiry")
        .eq("id", input.item_id)
        .single();

      const stockData: ProductStockInsert = {
        item_id: input.item_id,
        clinic_id: clinicId,
        quantity: input.quantity,
        deleted_at: null,
        deleted_by: null,
      };

      // Only set expiry date if item tracks expiry
      if (item?.track_by_expiry && input.expiry_date) {
        stockData.min_expiry_date = input.expiry_date;
        stockData.has_expiring_items = true; // Will be calculated properly later
      }

      if (existing) {
        // Update existing stock
        const { data, error } = await supabase
          .from("product_stock")
          .update(stockData as ProductStockUpdate)
          .eq("id", existing.id)
          .select(`
            *,
            items:item_id (
              id,
              name,
              unit,
              track_by_batch,
              track_by_expiry,
              product_subtype_id,
              product_subtype:product_subtypes(name)
            )
          `)
          .single();

        if (error) throw error;
        await relabelLatestStockMovementAsAdjustment({
          clinicId,
          itemId: input.item_id,
          previousQuantity: Number(existing.quantity ?? 0),
          nextQuantity: input.quantity,
          reason: input.adjustment_reason,
        });
        return data as unknown as ProductStock;
      } else {
        // Insert new stock
        const { data, error } = await supabase
          .from("product_stock")
          .insert([stockData])
          .select(`
            *,
            items:item_id (
              id,
              name,
              unit,
              track_by_batch,
              track_by_expiry,
              product_subtype_id,
              product_subtype:product_subtypes(name)
            )
          `)
          .single();

        if (error) throw error;
        await relabelLatestStockMovementAsAdjustment({
          clinicId,
          itemId: input.item_id,
          previousQuantity: 0,
          nextQuantity: input.quantity,
          reason: input.adjustment_reason,
        });
        return data as unknown as ProductStock;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      queryClient.invalidateQueries({ queryKey: ["stock-movements"] });
      toast.success("Estoque atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar estoque: ${error.message}`);
    },
  });
}

/**
 * Hook to update product stock quantity
 */
export function useUpdateProductStockQuantity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      quantity,
    }: {
      itemId: string;
      quantity: number;
    }) => {
      const clinicId = await getCurrentClinicId();

      const { data, error } = await supabase
        .from("product_stock")
        .update({ quantity, deleted_at: null, deleted_by: null } as ProductStockUpdate)
        .eq("item_id", itemId)
        .eq("clinic_id", clinicId)
        .select(`
          *,
          items:item_id (
            id,
            name,
            unit,
            track_by_batch,
            track_by_expiry,
            product_subtype_id,
            product_subtype:product_subtypes(name)
          )
        `)
        .single();

      if (error) throw error;
      return data as unknown as ProductStock;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      toast.success("Quantidade atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar quantidade: ${error.message}`);
    },
  });
}

/**
 * Hook to delete product stock
 */
export function useDeleteProductStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const clinicId = await getCurrentClinicId();

      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      const userId = authData?.user?.id ?? null;

      const { error } = await supabase
        .from("product_stock")
        .update(
          {
            deleted_at: new Date().toISOString(),
            deleted_by: userId,
          } as ProductStockUpdate
        )
        .eq("item_id", itemId)
        .eq("clinic_id", clinicId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-stock"] });
      toast.success("Item removido do estoque!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover item: ${error.message}`);
    },
  });
}
